'use client'

import posthog, { type PostHogConfig } from 'posthog-js'
import { PostHogProvider as PHProvider } from 'posthog-js/react'
// R2: identity comes from Supabase. identify() keys on the Supabase user id with email as a
// person property — PostHog person-merge on email preserves pre-migration Clerk history.
import { useSupabaseAuth } from '@/context/supabaseContext'
import { getStoredAnalyticsConsent } from '@/ui/components/AnalyticsConsent/AnalyticsConsent'
import { getVisitorRegion } from '@/lib/analytics/region'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { startOutboundLinkTracking } from '@/lib/analytics/outboundLinks'

const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY

// Cookieless server-side counting switch (analytics round 2, fix 7), read at BUILD time.
// next.config.js derives it from SERVER_PAGEVIEW_ENABLED, the same switch that turns on the
// middleware's server_pageview, so the two halves ship together or not at all.
const SERVER_PAGEVIEW = process.env.NEXT_PUBLIC_SERVER_PAGEVIEW === '1'

const BASE_CONFIG: Partial<PostHogConfig> = {
  api_host: 'https://us.i.posthog.com',
  autocapture: false,
  // 'history_change' also captures client-side route changes — with plain `true`
  // only full loads were pageviews, so SPA navigation was invisible (found 2026-08-26)
  capture_pageview: 'history_change',
  capture_pageleave: true,
  persistence: 'localStorage+cookie',
  loaded: (ph) => {
    if (process.env.NODE_ENV === 'development') {
      ph.debug()
    }
  },
}

/**
 * Fix 7, Europe with server-side counting on: posthog-js is not initialised before Accept.
 * An initialised but opted-out instance still fetches its remote config and flags from the
 * PostHog host (posthog-js 1.396.5: RemoteConfigLoader runs whatever the consent) and, without
 * opt_out_persistence_by_default, writes its cookie, so it would still send the visitor's IP
 * to the US host. On Accept the instance starts with opt_out_capturing_by_default and WITHOUT
 * cookieless_mode, then opt_in_capturing() switches it on ($opt_in + this page's $pageview,
 * as the cookieless path did).
 */
function startAfterAccept(key: string) {
  posthog.init(key, {
    ...BASE_CONFIG,
    opt_out_capturing_by_default: true,
    // a later Decline in the same visit also removes PostHog's cookie
    opt_out_persistence_by_default: true,
  })
  posthog.opt_in_capturing()
}

function PostHogInit() {
  const { user, isLoaded } = useSupabaseAuth()
  const identifiedRef = useRef(false)
  const [trackingMode, setTrackingMode] = useState<'full' | 'cookieless' | 'off' | null>(null)

  // Consent model (2026-09-17, privacy policy §3 must stay in sync with this):
  // - outside Europe: full tracking from the first page (unless the visitor declined before)
  // - Europe: cookieless counting (no cookies/localStorage, daily server-side hash) until the
  //   visitor clicks "Accept" on the banner, full tracking after; "Decline" stays cookieless.
  // Cookieless events are dropped unless the PostHog project has cookieless mode enabled.
  // With NEXT_PUBLIC_SERVER_PAGEVIEW=1 (fix 7) Europe changes: no stored choice or Decline =
  // no posthog-js at all, nothing leaves the browser (the middleware's server_pageview counts
  // the visit); Accept = full tracking as before. Outside Europe is unchanged.
  useEffect(() => {
    if (!POSTHOG_KEY) return
    // dev servers polluted prod analytics for months — never init on localhost
    // (NEXT_PUBLIC_POSTHOG_DEBUG=1 re-enables for deliberate local testing)
    if (
      /^(localhost|127\.)/.test(window.location.hostname) &&
      process.env.NEXT_PUBLIC_POSTHOG_DEBUG !== '1'
    )
      return

    let cancelled = false

    const onConsentChange = (event: CustomEvent<{ consent: boolean }>) => {
      if (!posthog.__loaded) {
        // fix 7: in Europe a visitor's Accept is what starts posthog-js
        if (SERVER_PAGEVIEW && event.detail.consent) {
          startAfterAccept(POSTHOG_KEY)
          setTrackingMode('full')
        }
        return
      }
      if (event.detail.consent) {
        if (!posthog.has_opted_in_capturing()) posthog.opt_in_capturing()
        setTrackingMode('full')
      } else {
        posthog.opt_out_capturing()
        setTrackingMode(posthog.config.cookieless_mode ? 'cookieless' : 'off')
      }
    }

    getVisitorRegion().then(region => {
      if (cancelled || posthog.__loaded) return
      const consent = getStoredAnalyticsConsent()
      const fullTracking = consent === true || (consent === null && region === 'other')

      if (SERVER_PAGEVIEW && !fullTracking && region === 'eu') {
        // fix 7: no pre-consent capture in Europe, wait for Accept (onConsentChange)
        setTrackingMode('off')
        return
      }

      posthog.init(POSTHOG_KEY, {
        ...BASE_CONFIG,
        // Only visitors without consent start cookieless: switching a consented visitor's
        // instance into on_reject would reset their existing distinct id on opt-in.
        // on_reject alone captures NOTHING while consent is pending; the opt-out default is
        // what turns "not answered yet" into cookieless capture (posthog-core _inCookielessMode).
        ...(fullTracking
          ? {}
          : { cookieless_mode: 'on_reject' as const, opt_out_capturing_by_default: true }),
      })
      setTrackingMode(fullTracking ? 'full' : 'cookieless')

      // Re-sync PostHog's own stored opt-in/out with the banner choice
      if (fullTracking && posthog.has_opted_out_capturing()) {
        posthog.opt_in_capturing({ captureEventName: false })
      } else if (consent === false && !posthog.has_opted_out_capturing()) {
        posthog.opt_out_capturing()
      }
    })

    window.addEventListener('analytics-consent-change', onConsentChange as EventListener)
    return () => {
      cancelled = true
      window.removeEventListener('analytics-consent-change', onConsentChange as EventListener)
    }
  }, [])

  // Clicks that leave the site (vendor websites, LinkedIn, YouTube, email) feed the
  // vendor reports. The listener only captures once PostHog has loaded.
  useEffect(() => {
    if (!POSTHOG_KEY) return
    return startOutboundLinkTracking()
  }, [])

  // Identify with the Supabase identity once the session hydrates. Never in cookieless
  // mode: a person profile needs the full-tracking consent (or a non-European visitor).
  useEffect(() => {
    if (!POSTHOG_KEY || !isLoaded || !posthog.__loaded || trackingMode !== 'full') return
    if (!posthog.has_opted_in_capturing()) return

    if (user && !identifiedRef.current) {
      posthog.identify(user.id, {
        email: user.email,
        name:
          (user.user_metadata?.full_name as string | undefined) ||
          (user.user_metadata?.name as string | undefined),
        auth_provider: user.app_metadata?.provider,
        signup_source: 'Platform Login',
      })
      identifiedRef.current = true
    }

    // Reset on sign-out
    if (!user && identifiedRef.current) {
      posthog.reset()
      identifiedRef.current = false
    }
  }, [user, isLoaded, trackingMode])

  return null
}

export function PostHogProvider({ children }: { children: ReactNode }) {
  if (!POSTHOG_KEY) {
    return <>{children}</>
  }

  return (
    <PHProvider client={posthog}>
      <PostHogInit />
      {children}
    </PHProvider>
  )
}
